import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents, VariantSelectedEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';
import VariantPicker from '@theme/variant-picker';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {AbortController} */
  #cartUpdateAbortController = new AbortController();

  get productPageUrl() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const hotspotProduct = /** @type {import('./product-hotspot').ProductHotspotComponent | null} */ (
      this.closest('product-hotspot-component')
    );
    const productLink = productCard?.getProductCardLink() || hotspotProduct?.getHotspotProductLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    // Fuller Archive v1.1.12: product-card variant links can include Shopify's
    // section rendering parameter. Quick-add needs the full product page markup,
    // not the product-card fragment, so remove it before fetching modal content.
    url.searchParams.delete('section_id');

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#cartUpdateAbortController.signal,
    });
    document.addEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
    this.#cartUpdateAbortController.abort();
    document.removeEventListener(ThemeEvents.variantSelected, this.#updateQuickAddButtonState.bind(this));
  }

  /**
   * Clears the cached content when cart is updated
   */
  #handleCartUpdate = () => {
    this.#cachedContent.clear();
  };

  /**
   * Re-renders the variant picker in the quick-add modal.
   * @param {Element} newHtml - The element to re-render.
   */
  #updateVariantPicker(newHtml) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;
    const variantPicker = /** @type {VariantPicker | null} */ (modalContent.querySelector('variant-picker'));
    if (!variantPicker) return;
    variantPicker.updateVariantPicker(newHtml);
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

    const currentUrl = this.productPageUrl;

    // Fuller Archive v1.1.18: always use the purpose-built artwork chooser.
    // The fetched PDP product grid is too large for collection quick-add modals and
    // can push the variant selection/add-to-cart column off-canvas on desktop.
    await this.#openQuickAddFallback(currentUrl);
    return;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);

    if (!productGrid) {
      // Fetch and cache the content
      const html = await this.fetchProductPage(currentUrl);
      if (html) {
        const gridElement = html.querySelector('[data-product-grid-content]');
        if (gridElement) {
          // Cache the cloned element to avoid modifying the original
          productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
          this.#cachedContent.set(currentUrl, productGrid);
        }
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent);
      this.#updateVariantPicker(productGrid);
      this.#openQuickAddModal();
      return;
    }

    // Fuller Archive v1.1.14: keep the interaction in-page. If the native
    // product grid fragment is unavailable, build a styled chooser directly
    // from Shopify's product JSON so the popup still has artwork, variant
    // selection, price, and add-to-cart behavior.
    await this.#openQuickAddFallback(currentUrl);
  };

  async #openQuickAddFallback(currentUrl) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!modalContent) return;

    const loading = document.createElement('div');
    loading.className = 'quick-add-modal__fallback quick-add-modal__fallback--loading';
    const loadingLabel = document.createElement('p');
    loadingLabel.className = 'fa-overline';
    loadingLabel.textContent = 'Loading artwork options';
    loading.append(loadingLabel);
    modalContent.replaceChildren(loading);
    this.#openQuickAddModal();

    try {
      const response = await fetch(this.#getProductJsonUrl(currentUrl), { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error(`Failed to fetch product JSON: ${response.status}`);
      const product = await response.json();
      const fallback = this.#buildArtworkFallback(product, currentUrl);
      modalContent.replaceChildren(fallback);
    } catch (error) {
      console.warn(error);
      const fallback = this.#buildBasicFallback(currentUrl);
      modalContent.replaceChildren(fallback);
    }
  }

  #getProductJsonUrl(currentUrl) {
    const url = new URL(currentUrl, window.location.origin);
    url.search = '';
    url.hash = '';
    if (!url.pathname.endsWith('.js')) url.pathname = `${url.pathname.replace(/\/$/, '')}.js`;
    return url.toString();
  }

  #getCardArtworkImageSrc() {
    const productCard = this.closest('product-card');
    const activeImage = productCard?.querySelector('slideshow-slide[aria-hidden="false"] img') ||
      productCard?.querySelector('[data-product-media-container] img') ||
      productCard?.querySelector('img');

    if (activeImage instanceof HTMLImageElement) {
      return activeImage.currentSrc || activeImage.src || activeImage.getAttribute('src') || '';
    }

    return '';
  }

  #getVariantArtworkImageSrc(variant) {
    const featuredImage = variant?.featured_image;
    if (!featuredImage) return '';
    if (typeof featuredImage === 'string') return featuredImage;
    return featuredImage.src || featuredImage.url || '';
  }

  #getProductArtworkImageSrc(product, variant) {
    return this.#getCardArtworkImageSrc() ||
      this.#getVariantArtworkImageSrc(variant) ||
      product.featured_image ||
      product.images?.[0] ||
      '';
  }

  #buildBasicFallback(currentUrl) {
    const fallback = document.createElement('div');
    fallback.className = 'quick-add-modal__fallback fa-quick-add-fallback';

    const panel = document.createElement('div');
    panel.className = 'fa-quick-add-fallback__panel';

    const eyebrow = document.createElement('p');
    eyebrow.className = 'fa-overline';
    eyebrow.textContent = 'Choose options';

    const heading = document.createElement('h2');
    heading.textContent = this.dataset.productTitle || 'This artwork';

    const copy = document.createElement('p');
    copy.textContent = 'We could not load the variant selector inline. Open artwork details to choose available options.';

    const link = document.createElement('a');
    link.className = 'button fa-quick-add-fallback__button';
    link.href = currentUrl;
    link.textContent = 'View artwork details';

    panel.append(eyebrow, heading, copy, link);
    fallback.append(panel);
    return fallback;
  }

  #buildArtworkFallback(product, currentUrl) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const availableVariant = variants.find((variant) => variant.available) || variants[0];
    const selectedOptions = [...(availableVariant?.options || [])];

    const wrapper = document.createElement('div');
    wrapper.className = 'quick-add-modal__fallback fa-quick-add-fallback fa-quick-add-fallback--artwork';

    const media = document.createElement('div');
    media.className = 'fa-quick-add-fallback__media';

    const image = document.createElement('img');
    const featuredImage = this.#getProductArtworkImageSrc(product, availableVariant);
    if (featuredImage) {
      image.src = featuredImage;
      image.alt = product.title || this.dataset.productTitle || 'Artwork preview';
      image.loading = 'eager';
      image.decoding = 'async';
      image.dataset.fallbackImage = 'true';
      media.append(image);
    }

    const details = document.createElement('div');
    details.className = 'fa-quick-add-fallback__details';

    const eyebrow = document.createElement('p');
    eyebrow.className = 'fa-overline';
    eyebrow.textContent = variants.length > 1 ? 'Choose options' : 'Add artwork';

    const heading = document.createElement('h2');
    heading.textContent = product.title || this.dataset.productTitle || 'This artwork';

    const type = document.createElement('p');
    type.className = 'fa-quick-add-fallback__type';
    type.textContent = product.type || 'Fuller Archive artwork';

    const price = document.createElement('p');
    price.className = 'fa-quick-add-fallback__price';
    price.textContent = this.#formatMoney(availableVariant?.price || product.price || 0);

    const form = document.createElement('form');
    form.className = 'fa-quick-add-fallback__form';
    form.method = 'post';
    form.action = '/cart/add';

    const idInput = document.createElement('input');
    idInput.type = 'hidden';
    idInput.name = 'id';
    idInput.value = availableVariant?.id ? String(availableVariant.id) : '';

    const quantityInput = document.createElement('input');
    quantityInput.type = 'hidden';
    quantityInput.name = 'quantity';
    quantityInput.value = '1';

    const options = document.createElement('div');
    options.className = 'fa-quick-add-fallback__options';

    const productOptions = Array.isArray(product.options) ? product.options : [];
    productOptions.forEach((option, optionIndex) => {
      const values = Array.isArray(option.values) ? option.values : [];
      if (!values.length || (values.length === 1 && values[0] === 'Default Title')) return;

      const fieldset = document.createElement('fieldset');
      fieldset.className = 'fa-quick-add-fallback__option';
      fieldset.dataset.optionIndex = String(optionIndex);

      const legend = document.createElement('legend');
      legend.textContent = option.name || `Option ${optionIndex + 1}`;
      fieldset.append(legend);

      const valueList = document.createElement('div');
      valueList.className = 'fa-quick-add-fallback__values';

      values.forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'fa-quick-add-fallback__value';
        button.textContent = value;
        button.dataset.optionValue = value;
        button.setAttribute('aria-pressed', String(selectedOptions[optionIndex] === value));
        button.addEventListener('click', () => {
          selectedOptions[optionIndex] = value;
          this.#updateFallbackSelection(wrapper, variants, selectedOptions, idInput, price);
        });
        valueList.append(button);
      });

      fieldset.append(valueList);
      options.append(fieldset);
    });

    const button = document.createElement('button');
    button.type = 'submit';
    button.className = 'button fa-quick-add-fallback__button';
    button.textContent = availableVariant?.available === false ? 'Sold out' : 'Add to cart';
    button.disabled = availableVariant?.available === false;

    const detailsLink = document.createElement('a');
    detailsLink.className = 'fa-quick-add-fallback__details-link';
    detailsLink.href = currentUrl;
    detailsLink.textContent = 'View artwork details';

    form.append(idInput, quantityInput, options, button);
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!idInput.value || button.disabled) return;
      button.disabled = true;
      button.textContent = 'Adding…';
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form),
        });
        if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);
        button.textContent = 'Added';
        document.dispatchEvent(new Event(ThemeEvents.cartUpdate, { bubbles: true }));
        setTimeout(this.#closeQuickAddModal, 650);
      } catch (error) {
        console.warn(error);
        button.disabled = false;
        button.textContent = 'Try again';
      }
    });

    details.append(eyebrow, heading, type, price, form, detailsLink);
    wrapper.append(media, details);
    this.#updateFallbackSelection(wrapper, variants, selectedOptions, idInput, price);
    return wrapper;
  }

  #updateFallbackSelection(wrapper, variants, selectedOptions, idInput, priceElement) {
    const selectedVariant = variants.find((variant) =>
      selectedOptions.every((value, index) => !value || variant.options?.[index] === value)
    );

    const optionButtons = wrapper.querySelectorAll('.fa-quick-add-fallback__value');
    optionButtons.forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      const fieldset = button.closest('[data-option-index]');
      const optionIndex = Number(fieldset?.getAttribute('data-option-index'));
      const isActive = selectedOptions[optionIndex] === button.dataset.optionValue;
      button.setAttribute('aria-pressed', String(isActive));
    });

    const submit = wrapper.querySelector('.fa-quick-add-fallback__button');
    const fallbackImage = wrapper.querySelector('img[data-fallback-image="true"]');
    if (selectedVariant) {
      idInput.value = String(selectedVariant.id);
      priceElement.textContent = this.#formatMoney(selectedVariant.price || 0);
      const variantImageSrc = this.#getVariantArtworkImageSrc(selectedVariant);
      if (variantImageSrc && fallbackImage instanceof HTMLImageElement) fallbackImage.src = variantImageSrc;
      if (submit instanceof HTMLButtonElement) {
        submit.disabled = selectedVariant.available === false;
        submit.textContent = selectedVariant.available === false ? 'Sold out' : 'Add to cart';
      }
    } else if (submit instanceof HTMLButtonElement) {
      idInput.value = '';
      submit.disabled = true;
      submit.textContent = 'Unavailable';
    }
  }

  #formatMoney(cents) {
    const amount = Number(cents || 0) / 100;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  }

  #resetScroll() {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    const productDetails = dialogComponent.querySelector('.product-details');
    const productMedia = dialogComponent.querySelector('.product-information__media');
    productDetails?.scrollTo({ top: 0, behavior: 'instant' });
    productMedia?.scrollTo({ top: 0, behavior: 'instant' });
  }

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();

    // is nondeterministic when the open attribute is set on the dialog element after .showDialog() is called.
    // Waiting until the open animation starts seemed to be the most reliable metric here.
    const dialog = dialogComponent.refs?.dialog;
    if (!dialog) return;
    dialog.addEventListener('animationstart', this.#resetScroll.bind(this), { once: true });
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   */
  async updateQuickAddModal(productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');

    if (!productGrid || !modalContent) return;

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      if (productPrice) {
        productHeader.appendChild(productPrice);
      }
      productGrid.appendChild(productHeader);

      if (variantPicker) {
        productGrid.appendChild(variantPicker);
      }
      if (productFormComponent) {
        productGrid.appendChild(productFormComponent);
      }

      productDetails?.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Updates the quick-add button state based on whether a swatch is selected
   * @param {VariantSelectedEvent} event - The variant selected event
   */
  #updateQuickAddButtonState(event) {
    if (!(event.target instanceof HTMLElement)) return;
    if (event.target.closest('product-card') !== this.closest('product-card')) return;
    const productOptionsCount = this.dataset.productOptionsCount;
    const quickAddButton = productOptionsCount === '1' ? 'add' : 'choose';
    this.setAttribute('data-quick-add-button', quickAddButton);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }
}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
